import React from 'react';
import { observer } from 'mobx-react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from 'semantic-ui-react';
import If from 'Views/Components/If/If';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { PricingStrategyIds } from 'Global/Pricing/PricingStrategy';
import { PriceCellState } from 'Global/Pricing/PriceCellState';
import { PricingRouteTabs, SegmentHeaderCells } from './PricingRouteTabs';
import { UserPriceCell } from './UserPriceCell';
import { PricingStrategyKind, UserPriceRow } from './userPricingRows';

interface UserPassengerPricingTableProps {
	rows: UserPriceRow[];
	/** Upsert the target row's slot with the written cell state (see upsertPriceRow). */
	onCellWrite: (target: UserPriceRow, next: PriceCellState) => void;
	isReadOnly?: boolean;
	errors: { [key: string]: string | undefined };
	pricingStrategy: PricingStrategyKind;
}

/**
 * Form-state fork of the shared PassengerTable. Where the legacy table pushed placeholder
 * PriceEntity rows into the observable array mid-render, this one renders a virtual row for
 * any missing (passenger type × route/segment) slot and lets onCellWrite materialise it in
 * form state on first edit.
 */
export const UserPassengerPricingTable = observer(
	({ rows, onCellWrite, isReadOnly = false, errors, pricingStrategy }: UserPassengerPricingTableProps) => {
		const findOrVirtual = (
			passengerTypeId: string,
			routeId: string | undefined,
			startStopId?: string,
		): UserPriceRow => {
			const found = rows.find(
				row =>
					row.passengerTypeId === passengerTypeId &&
					(routeId ? row.routeId === routeId : !row.routeId) &&
					(startStopId === undefined || row.startStopId === startStopId),
			);
			return (
				found ?? {
					passengerTypeId,
					routeId,
					startStopId,
					amount: null,
					mode: 'FIXED',
					pricingStrategyId: PricingStrategyIds[pricingStrategy],
				}
			);
		};

		return (
			<PricingRouteTabs pricingStrategy={pricingStrategy}>
				{(route, segmentsSorted) => (
					<Table unstackable key={route.id}>
						<TableHeader>
							<TableRow>
								<TableCell textAlign="left">Ticket type</TableCell>
								<If condition={pricingStrategy === 'Fixed'}>
									<TableCell textAlign="left">Cost</TableCell>
								</If>
								<If condition={pricingStrategy === 'ConsecutiveStops'}>
									<SegmentHeaderCells segments={segmentsSorted} />
								</If>
							</TableRow>
						</TableHeader>
						<TableBody>
							{passengerTypeStore.passengerTypes.map((x, rowIndex) => {
								const name = passengerTypeStore.getSingularName(x.passengerTypeKey);
								return (
									<TableRow key={x.id}>
										<TableCell textAlign="left">{name}</TableCell>
										<If condition={pricingStrategy === 'Fixed'}>
											<TableCell textAlign="left">
												{(() => {
													const cellRow = findOrVirtual(x.id, route.id);
													return (
														<UserPriceCell
															row={cellRow}
															onWrite={next => onCellWrite(cellRow, next)}
															isReadOnly={isReadOnly}
															error={!!errors?.amount}
															testId={`account-passenger-price-cell-${rowIndex}`}
														/>
													);
												})()}
											</TableCell>
										</If>
										{pricingStrategy === 'ConsecutiveStops' &&
											segmentsSorted.map(segment => {
												const cellRow = findOrVirtual(x.id, route.id, segment.startStop.id);
												return (
													<TableCell textAlign="left" key={segment.id}>
														<UserPriceCell
															row={cellRow}
															onWrite={next => onCellWrite(cellRow, next)}
															isReadOnly={isReadOnly}
															error={!!errors?.amount}
														/>
													</TableCell>
												);
											})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
			</PricingRouteTabs>
		);
	},
);
